import { useState } from "react";
import { Loader2, RefreshCw, Landmark, Lock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const TONE = {
  not_ready: "bg-slate-100 text-slate-500 border-slate-200",
  ready: "bg-amber-50 text-amber-800 border-amber-200",
  pushed: "bg-blue-50 text-blue-800 border-blue-200",
  failed: "bg-rose-50 text-rose-800 border-rose-200",
  verified: "bg-emerald-50 text-emerald-800 border-emerald-200",
};
const LABEL = { not_ready: "Not ready", ready: "Ready to send", pushed: "Staged in Foundation", failed: "Failed", verified: "Imported in Foundation" };
export const LOCKED = ["pushed", "verified"];
export const isSyncLocked = (sync) => LOCKED.includes(sync?.status);

// CO / PO Foundation sync state — read-only on the Work Order; accounting sends from Settings › Integrations › Foundation › Sync.
// Staged / imported documents are LOCKED; a PO edited after it was sent shows "Changed since sent".
export function FoundationSyncPill({ sync, onRetry, testId = "foundation-sync", showLock = true }) {
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState(sync);
  const s = state || sync;
  if (!s?.status) return null;
  const label = s.label || LABEL[s.status] || s.status;
  const locked = showLock && isSyncLocked(s);
  const changed = s.changed_after_sync?.deltas?.length || 0;
  const retry = async () => {
    setBusy(true);
    try {
      const r = await onRetry();
      setState(r?.data || r);
      toast[(r?.data || r)?.status === "failed" ? "error" : "success"]((r?.data || r)?.message || "Retried");
    } catch (e) { toast.error(e?.response?.data?.detail || "Retry failed"); }
    finally { setBusy(false); }
  };
  return (
    <span className="inline-flex items-center gap-1" data-testid={testId}>
      <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-medium inline-flex items-center gap-1 ${TONE[s.status] || TONE.not_ready}`}
            title={`${s.message || ""}${locked ? " · Locked — synced to Foundation" : ""}`} data-testid={`${testId}-status`}>
        {locked ? <Lock className="w-2.5 h-2.5" data-testid={`${testId}-lock`} /> : <Landmark className="w-2.5 h-2.5" />} {label}
      </span>
      {changed > 0 && (
        <span className="text-[10px] px-1.5 py-0.5 rounded-full border font-medium inline-flex items-center gap-1 bg-amber-50 text-amber-900 border-amber-300"
              title={s.changed_after_sync.deltas.map((d) => `${d.label}: ${d.from ?? "—"} → ${d.to ?? "—"}`).join("\n")} data-testid={`${testId}-changed`}>
          <AlertTriangle className="w-2.5 h-2.5" /> Changed since sent ({changed})
        </span>
      )}
      {s.message && !locked && <span className="text-[10px] text-slate-500 truncate max-w-[260px]" title={s.message} data-testid={`${testId}-message`}>{s.message}</span>}
      {onRetry && (s.status === "ready" || s.status === "failed") && (
        <button type="button" onClick={retry} disabled={busy} className="text-[10px] text-blue-600 hover:underline inline-flex items-center gap-0.5 disabled:opacity-50" data-testid={`${testId}-retry`}>
          {busy ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <RefreshCw className="w-2.5 h-2.5" />} {s.status === "failed" ? "Retry" : "Push now"}
        </button>
      )}
    </span>
  );
}
